(() => {
  if (window.PuzzlePupOfferEngine) return;

  const route = path => `${window.Shopify?.routes?.root || '/'}${path}`;
  const track = (eventName, payload) => window.PuzzlePupAnalytics?.track(eventName, payload);

  async function request(path, options = {}) {
    const response = await fetch(route(path), {
      credentials: 'same-origin',
      ...options,
      headers: { Accept: 'application/json', ...(options.headers || {}) }
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.description || payload.message || 'The cart could not be updated.');
    return payload;
  }

  const cart = () => request('cart.js');
  // Single source of truth for the Mat + 50%-off-jar package variants (keyed by jar variant id).
  const matAddonPackages = Object.freeze({
    starter: { 45943769956492: 62502485262476, 45943804461196: 62502485393548, 45943912857740: 62502485426316 },
    double: { 45943769956492: 62502485459084, 45943804461196: 62502485491852, 45943912857740: 62502485524620 }
  });

  async function add(items) {
    return request('cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
  }

  async function removeLines(lines) {
    for (const line of lines) {
      await request('cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: 0 })
      });
    }
  }

  async function refreshCartSurfaces() {
    if (typeof window.PuzzlePupStorefront?.refreshCartSurfaces === 'function') {
      await window.PuzzlePupStorefront.refreshCartSurfaces();
    }
    document.dispatchEvent(new CustomEvent('pp:cart-updated'));
  }

  function requestItem(item, token) {
    const output = {
      id: Number(item.id),
      quantity: Number(item.quantity || 1),
      properties: {
        ...(item.properties || {}),
        '_PuzzlePup verification': token
      }
    };
    if (item.selling_plan) output.selling_plan = Number(item.selling_plan);
    return output;
  }

  async function addAndVerify({
    items,
    expectedMaxIncrease,
    expectedCartTotal,
    source = 'storefront',
    offerId = 'purchase',
    successEvent = 'pp_offer_accept',
    failureMessage = 'Shopify did not confirm the offer price, so the added items were removed and you were not charged.'
  }) {
    if (!Array.isArray(items) || !items.length) throw new Error('Choose at least one item.');
    const formulaIds = [45943769956492, 45943804461196, 45943912857740];
    if (source === 'cart_upsell' && items.length === 1 && formulaIds.includes(Number(items[0].id)) && !items[0].selling_plan) {
      const current = await cart();
      const starter = current.items.find(item => item.product_id === 9493280653452);
      const mats = current.items.filter(item => !item.selling_plan_allocation && item.product_id === 8930762096780);
      const matCount = mats.reduce((sum, item) => sum + Number(item.quantity), 0);
      if (starter || matCount) return upgradeMatPackage({ formulaVariant: Number(items[0].id), kind: !starter && matCount >= 2 ? 'double' : 'starter', source });
    }
    if (source === 'cart_upsell' && items.length === 1 && [46206663098508, 50719270404236].includes(Number(items[0].id)) && !items[0].selling_plan) {
      const current = await cart();
      const jar = current.items.find(item => formulaIds.includes(Number(item.variant_id)) && !item.selling_plan_allocation && Number(item.quantity) === 1);
      if (jar) return upgradeMatPackage({ formulaVariant: Number(jar.variant_id), kind: 'starter', source, fromJar: true });
    }
    const matItem = items.find(item => [46206663098508, 50719270404236].includes(Number(item.id)));
    const jarItem = items.find(item => formulaIds.includes(Number(item.id)) && !item.selling_plan);
    if (matItem && jarItem && items.length === 2 && Number(jarItem.quantity || 1) === 1 && !matItem.selling_plan) {
      const kind = Number(matItem.quantity || 1) === 2 ? 'double' : 'starter';
      if (Number(matItem.quantity || 1) <= 2) items = [{ id: matAddonPackages[kind][Number(jarItem.id)], quantity: 1, properties: { ...matItem.properties, ...jarItem.properties, '_PuzzlePup offer': 'Mat package with 50% off jar' } }];
    }
    const before = await cart();
    const token = `pp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const outgoing = items.map(item => requestItem(item, token));
    await add(outgoing);
    try {
      await window.PuzzlePupSeptemberGift?.sync?.({ refresh: false });
    } catch (giftError) {
      const failedCart = await cart();
      const failedLines = failedCart.items.filter(item => item.properties?.['_PuzzlePup verification'] === token);
      if (failedLines.length) await removeLines(failedLines);
      await window.PuzzlePupSeptemberGift?.sync?.({ refresh: false }).catch(() => {});
      await refreshCartSurfaces();
      throw giftError;
    }
    const after = await cart();
    const taggedLines = after.items.filter(item => item.properties?.['_PuzzlePup verification'] === token);
    const addedQuantity = taggedLines.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const requestedQuantity = outgoing.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const taggedFinalTotal = taggedLines.reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);
    const maximumIncrease = Number.isFinite(Number(expectedMaxIncrease))
      ? Number(expectedMaxIncrease)
      : items.reduce((sum, item) => sum + Number(item.expectedLineTotal ?? item.expectedPrice ?? 0), 0);
    const totalConfirmed = Number.isFinite(Number(expectedCartTotal))
      ? Number(after.total_price) <= Number(expectedCartTotal)
      : Number(after.total_price) <= Number(before.total_price) + maximumIncrease;
    const linesConfirmed = taggedLines.length > 0 && addedQuantity >= requestedQuantity;
    const taggedTotalConfirmed = Number.isFinite(Number(expectedCartTotal))
      ? totalConfirmed
      : maximumIncrease <= 0 || taggedFinalTotal <= maximumIncrease;

    if (!linesConfirmed || !totalConfirmed || !taggedTotalConfirmed) {
      if (taggedLines.length) await removeLines(taggedLines);
      await refreshCartSurfaces();
      track('pp_offer_reject', {
        offer_id: offerId,
        source,
        requested_quantity: requestedQuantity,
        expected_max_increase: maximumIncrease,
        actual_increase: Number(after.total_price) - Number(before.total_price),
        reason: 'price_not_confirmed'
      });
      throw new Error(failureMessage);
    }

    track(successEvent, {
      offer_id: offerId,
      source,
      requested_quantity: requestedQuantity,
      expected_max_increase: maximumIncrease,
      actual_increase: Number(after.total_price) - Number(before.total_price),
      resulting_cart_total: Number(after.total_price),
      verified_by_shopify: true
    });
    return { before, cart: after, lines: taggedLines, token };
  }

  let matUpgradeBusy = false;
  async function upgradeMatPackage({ formulaVariant, kind = 'starter', source = 'cart_upsell', fromJar = false }) {
    if (matUpgradeBusy) throw new Error('Your package is still being updated.');
    const variant = matAddonPackages[kind]?.[formulaVariant];
    if (!variant) throw new Error('Choose an available formula.');
    matUpgradeBusy = true;
    const removedUnits = [];
    let token;
    try {
      const before = await cart();
      const mats = before.items.filter(item => !item.selling_plan_allocation && item.product_id === 8930762096780);
      const starter = before.items.find(item => !item.selling_plan_allocation && item.product_id === 9493280653452);
      const jars = before.items.filter(item => !item.selling_plan_allocation && Number(item.variant_id) === Number(formulaVariant));
      const qualifying = fromJar ? jars : (kind === 'starter' && starter ? [starter] : mats);
      let remaining = kind === 'double' ? 2 : 1;
      const replacements = [];
      for (const item of qualifying) {
        const quantity = Math.min(remaining, Number(item.quantity));
        if (quantity > 0) replacements.push({ item, quantity });
        remaining -= quantity;
        if (remaining <= 0) break;
      }
      if (remaining > 0) throw new Error(fromJar ? 'Add the matching jar first.' : 'Add the matching Mat package first.');
      const packagePrice = kind === 'double' ? 7748 : 5748;
      const replacedTotal = replacements.reduce((sum, row) => sum + Math.round(row.item.final_line_price * row.quantity / row.item.quantity), 0);
      const maximumTotal = before.total_price - replacedTotal + packagePrice;
      token = 'pp-mat-upgrade-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
      const updates = Object.fromEntries(replacements.map(row => [row.item.key, row.item.quantity - row.quantity]));
      await request('cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) });
      removedUnits.push(...replacements);
      await add([{ id: variant, quantity: 1, properties: { '_PuzzlePup verification': token, '_PuzzlePup offer': 'Mat package with 50% off jar' } }]);
      await window.PuzzlePupSeptemberGift?.sync?.({ refresh: false });
      const after = await cart();
      const lines = after.items.filter(item => item.properties?.['_PuzzlePup verification'] === token);
      const packageTotal = lines.reduce((sum, item) => sum + item.final_line_price, 0);
      if (!lines.length || packageTotal > packagePrice || after.total_price > maximumTotal) {
        throw new Error('The Mat and jar offer price could not be confirmed.');
      }
      track('pp_offer_verified', { offer_id: 'mat-jar-' + kind, source,
        actual_increase: after.total_price - before.total_price, resulting_cart_total: after.total_price, verified_by_shopify: true });
      return { before, cart: after, lines, token };
    } catch (error) {
      if (token) {
        const current = await cart();
        const added = current.items.filter(item => item.properties?.['_PuzzlePup verification'] === token);
        if (added.length) await removeLines(added);
      }
      for (const row of removedUnits) {
        await add([{ id: row.item.variant_id, quantity: row.quantity, properties: row.item.properties || {} }]);
      }
      await window.PuzzlePupSeptemberGift?.sync?.({ refresh: false }).catch(() => {});
      await refreshCartSurfaces();
      throw error;
    } finally { matUpgradeBusy = false; }
  }

  window.PuzzlePupOfferEngine = Object.freeze({ addAndVerify, upgradeMatPackage, cart, refreshCartSurfaces, matPackages: matAddonPackages });
})();


(() => {
  const packages = window.PuzzlePupOfferEngine.matPackages;
  function init() {
    document.querySelectorAll('cart-drawer details.pp-discount-entry').forEach(details => {
      if (details.dataset.ppCompactDiscountReady) return;
      details.dataset.ppCompactDiscountReady = 'true';
      const applied = details.querySelector('[data-applied-codes]')?.dataset.appliedCodes;
      if (applied && window.matchMedia('(max-width:749px)').matches) details.open = false;
    });
    document.querySelectorAll('[data-pp-v22][data-kind="snuffle"],[data-pp-buybox][data-product-type="mat"]').forEach(root => {
      if (root.dataset.ppFixedMatPackageReady) return;
      const offer = root.querySelector('[data-pp-mat-addon]');
      const checkbox = offer?.querySelector('[data-pp-mat-addon-check]');
      const select = offer?.querySelector('[data-pp-mat-addon-select]');
      const button = root.querySelector('[data-pp-v22-add],[data-pp-product-submit]');
      if (!checkbox || !select || !button) return;
      // Canonical Mat pages with priced extras use their own purchase handler.
      if (root.querySelector('[data-pp-canonical-form]') && select.querySelector('option[data-addon-price-cents]')) return;
      root.dataset.ppFixedMatPackageReady = 'true';
      const feedback = document.createElement('span');
      feedback.setAttribute('role', 'status');
      feedback.setAttribute('aria-live', 'polite');
      offer.appendChild(feedback);
      let busy = false;
      let wantsJar = checkbox.checked;
      const isDouble = () => Boolean(root.querySelector('[data-package="double"][aria-pressed="true"],[data-pp-package][value="two-mats"]:checked'));
      const setText = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };
      function refresh() {
        if (offer.hidden) offer.hidden = false;
        checkbox.checked = wantsJar;
        const terms = offer.querySelector('.pp-mat-addon__terms');
        setText(terms, '50% off the regular $34.99 jar. One-time purchase; one jar with each Mat package.');
        offer.querySelectorAll('[data-pp-mat-addon-image]').forEach(img => { img.hidden = img.dataset.ppMatAddonImage !== select.value; });
        setText(offer.querySelector('[data-pp-mat-addon-benefit]'), select.selectedOptions[0]?.dataset.addonBenefit || '');
        if (!busy) {
          const double = isDouble();
          const price = wantsJar ? (double ? '$77.48' : '$57.48') : (double ? '$59.99' : '$39.99');
          const title = double ? 'Two Snuffle Mats' : 'One Snuffle Mat';
          const selectedTitle = title + (wantsJar ? ' + ' + (select.selectedOptions[0]?.dataset.addonName || 'selected jar') : '');
          setText(button, root.matches('[data-pp-v22]')
            ? (double ? 'Add 2 Mats' : 'Add 1 Mat') + (wantsJar ? ' + jar' : '') + ' · ' + price
            : 'Add to cart · ' + price);
          root.querySelectorAll('[data-pp-display-price],[data-pp-total-due],[data-pp-sticky-price],[data-pp-drawer-price]').forEach(node => setText(node, price));
          setText(root.querySelector('[data-pp-savings-line]'), selectedTitle + ' · ' + price);
          setText(root.querySelector('[data-pp-v22-snuffle-summary]'), double
            ? 'Two full-size Mats, second Mat 50% off' + (wantsJar ? ', and your selected jar' : '') + ' · ' + price + '. One-time purchase.'
            : wantsJar ? 'Your Mat and selected jar · ' + price + '. One-time purchase.'
              : 'One premium Mat · ' + price + '. One-time purchase.');
        }
      }
      root.addEventListener('change', event => {
        if (event.target === checkbox) wantsJar = checkbox.checked;
        if (isDouble() && (event.target === checkbox || event.target === select)) {
          event.stopImmediatePropagation(); refresh();
        }
      }, true);
      root.addEventListener('change', () => window.setTimeout(refresh, 0));
      root.addEventListener('click', event => {
        if (!event.target.closest('[data-pp-v22-add],[data-pp-product-submit]') || !checkbox.checked) {
          window.setTimeout(refresh, 0); return;
        }
        event.preventDefault(); event.stopImmediatePropagation();
        if (!busy) purchase();
      }, true);
      root.querySelector('[data-pp-product-form]')?.addEventListener('submit', event => {
        if (!checkbox.checked) return;
        event.preventDefault(); event.stopImmediatePropagation();
        if (!busy) purchase();
      }, true);
      new MutationObserver(refresh).observe(offer, { attributes: true, attributeFilter: ['hidden'] });
      async function purchase() {
        busy = true; button.disabled = true; button.textContent = 'Adding and checking price...'; feedback.textContent = '';
        const kind = isDouble() ? 'double' : 'starter';
        try {
          const verified = await window.PuzzlePupOfferEngine.addAndVerify({
            items: [{ id: packages[kind][Number(select.value)], quantity: 1, properties: { '_PuzzlePup offer': 'Mat package with 50% off jar' } }],
            expectedMaxIncrease: kind === 'double' ? 7748 : 5748,
            source: root.matches('[data-pp-v22]') ? 'snuffle_funnel' : 'mat_pdp',
            offerId: 'mat-jar-' + kind, successEvent: 'pp_offer_verified'
          });
          if (root.matches('[data-pp-buybox]')) window.PuzzlePupAnalytics?.track('pp_add_to_cart', {
            variant_id: packages[kind][Number(select.value)], package: 'mat-jar-' + kind,
            purchase_type: 'one-time', quantity: 1, effective_price: kind === 'double' ? 7748 : 5748,
            resulting_cart_total: Number(verified.cart?.total_price || 0)
          });
          await window.PuzzlePupOfferEngine.refreshCartSurfaces();
          window.PuzzlePupStorefront?.openCartDrawer?.();
        } catch (error) { feedback.textContent = error.message || 'Your package could not be added.'; }
        finally { busy = false; button.disabled = false; refresh(); }
      }
      refresh();
    });
    document.querySelectorAll('[data-pp-offer-card]').forEach(card => {
      const button = card.querySelector('[data-pp-upsell-add]');
      const formula = Number(button?.dataset.variant);
      if (Number(card.dataset.effectivePrice) !== 1749 || !packages.starter[formula]) return;
      const terms = card.querySelector('small');
      if (terms) terms.textContent = 'One-time jar with your Mat package.';
      card.dataset.offerStatus = 'One-time jar with your Mat package.';
      const retitle = () => {
        if (!button || button.disabled) return;
        if (button.textContent !== 'Add') button.textContent = 'Add';
        const name = (card.querySelector('h3')?.textContent.trim() || 'Add a jar') + ' · $17.49 extra';
        if (button.getAttribute('aria-label') !== name) button.setAttribute('aria-label', name);
      };
      retitle();
      if (button && !button.dataset.ppFixedJarLabelReady) {
        button.dataset.ppFixedJarLabelReady = 'true';
        new MutationObserver(retitle).observe(button, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-label'] });
      }
    });
    document.querySelectorAll('[data-pp-cart-upsells]').forEach(section => {
      const intro = section.querySelector('h2')?.nextElementSibling;
      if (intro?.tagName === 'P') intro.textContent = 'Choose a one-time extra for your order. Prices are shown before shipping and tax.';
    });
  }
  const start = () => {
    init();
    new MutationObserver(records => {
      if (records.some(record => Array.from(record.addedNodes).some(node => node.nodeType === 1 &&
        (node.matches('[data-pp-cart-upsells]') || node.querySelector('[data-pp-cart-upsells]'))))) init();
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
  document.addEventListener('pp:cart-updated', init);
  document.addEventListener('shopify:section:load', init);
})();

(() => {
  if (window.PuzzlePupUniformPurchase) return;
  const cfg = {
    formulas: {
      ALL: {name:'Allergy', id:45943769956492},
      HJ: {name:'Hip & Joint', id:45943804461196},
      SC: {name:'Skin & Coat', id:45943912857740}
    },
    pair: {'ALL+ALL':62378108813452,'ALL+HJ':62378108715148,'ALL+SC':62378108747916,'HJ+HJ':62378108682380,'HJ+SC':62378053173388,'SC+SC':62378108780684},
    trio: {'ALL+ALL+ALL':62510650228876,'ALL+ALL+HJ':62510650261644,'ALL+ALL+SC':62510650294412,'ALL+HJ+HJ':62510650327180,'ALL+HJ+SC':62510650359948,'ALL+SC+SC':62510650392716,'HJ+HJ+HJ':62510650425484,'HJ+HJ+SC':62510650458252,'HJ+SC+SC':62510650491020,'SC+SC+SC':62510650523788},
    plans: {
      1:[[3320578188,'Every month'],[3470786700,'Every 6 weeks'],[3470819468,'Every 2 months']],
      2:[[3747020940,'Every 60 days'],[3747053708,'Every 30 days'],[3747086476,'Every 40 days'],[3747119244,'Every 120 days'],[3747152012,'Every 20 days']],
      3:[[3756195980,'Every 90 days'],[3756228748,'Every 60 days'],[3756261516,'Every 180 days'],[3756294284,'Every 30 days']]
    },
    first:{1:2624,2:4199,3:5249},refill:{1:2799,2:4479,3:6298},once:{1:3499,2:4999,3:6999},discount:{1:25,2:40,3:50}
  };
  const money = n => '$'+(n/100).toFixed(2);
  function init() {
    document.querySelectorAll('[data-pp-uniform]').forEach(root=>{
      if(root.dataset.ready) return;
      root.dataset.ready='true';
      const params=new URLSearchParams(location.search);
      const picks=[...root.querySelectorAll('[data-pp-jar-formula]')];
      const base=cfg.formulas[params.get('formula')]?params.get('formula'):(root.dataset.formula||'ALL');
      picks.forEach(p=>{p.value=base});
      const requested=params.get('qty')||root.dataset.count||1;
      let count=Number(({one:1,two:2,three:3})[requested]||requested);
      if(![1,2,3].includes(count)) count=1;
      const requestedVariant=Number(params.get('variant'));
      const requestedMix=Object.entries(count===3?cfg.trio:cfg.pair).find(([,id])=>id===requestedVariant)?.[0];
      if(requestedMix&&count>1&&!params.has('formula'))requestedMix.split('+').forEach((k,i)=>{if(picks[i])picks[i].value=k;});
      let method=params.get('method')==='onetime'?'once':'sub',busy=false;
      const schedule=root.querySelector('[data-pp-delivery]');
      const terms=root.querySelector('[data-pp-uniform-terms]');
      const add=root.querySelector('[data-pp-uniform-add]');
      const status=root.querySelector('[data-pp-uniform-status]');
      const buybox=root.closest('[data-pp-buybox]');
      const productPage=buybox?.closest('[data-pp-v3-pdp]');
      function render(reset=false){
        root.querySelectorAll('[data-pp-uniform-count]').forEach(b=>{const n=Number(b.dataset.ppUniformCount);b.setAttribute('aria-pressed',String(count===n));b.querySelector('[data-pp-choice-price]').textContent=money((method==='sub'?cfg.first:cfg.once)[n]);b.querySelector('[data-pp-choice-save]').textContent=method==='sub'?`Save ${cfg.discount[n]}% today*`:n===2?'FREE Classic Mat':n===3?'Save $34.98':'Buy once';b.querySelector('[data-pp-choice-detail]').textContent=n+' '+(n===1?'sealed jar':'sealed jars')+(n===3?' · Best value':'');});
        root.querySelectorAll('[data-pp-uniform-method]').forEach(b=>b.setAttribute('aria-pressed',String(b.dataset.ppUniformMethod===method)));
        picks.forEach((p,i)=>{p.closest('label').hidden=i>=count;const img=p.closest('label').querySelector('img');if(img){const option=p.selectedOptions[0];img.src=option.dataset.image;img.alt='Selected '+cfg.formulas[p.value].name+' jar';}});
        const sub=method==='sub';
        root.querySelector('[data-pp-delivery-wrap]').hidden=!sub;
        schedule.disabled=!sub;
        if(reset){
          schedule.replaceChildren(...cfg.plans[count].map(([id,text])=>{const o=document.createElement('option');o.value=id;o.textContent=text;return o;}));
          const weightDefaults={small:{1:3470819468,2:3747119244,3:3756261516},large:{2:3747086476,3:3756228748}};
          const proposed=weightDefaults[params.get('weight')]?.[count];
          if(proposed&&cfg.plans[count].some(([id])=>id===proposed))schedule.value=String(proposed);
        }
        terms.textContent=sub?`${money(cfg.first[count])} today, then ${money(cfg.refill[count])} per delivery ${schedule.selectedOptions[0]?.textContent.toLowerCase()||''} until canceled. Free U.S. shipping. Skip, pause or cancel before your next charge.`:count===2?'$49.99 once for any two jars, plus a FREE round Classic Mat while gifts last. Free U.S. shipping.':`${money(cfg.once[count])} once for ${count===1?'one jar':'three jars'}. Free U.S. shipping.`;
        root.querySelector('[data-pp-uniform-comparison]').textContent=sub?'*Savings compare with '+count+' single '+(count===1?'jar':'jars')+' bought once at $34.99 each. Refills save '+({1:20,2:36,3:40}[count])+'% against that total.':'Package savings compare with individual jars at $34.99 each.';
        add.textContent=busy?'Adding…':'Add to Cart | '+money((sub?cfg.first:cfg.once)[count]);
        add.disabled=busy;
        const display=buybox?.querySelector('[data-pp-display-price]');
        if(display)display.textContent=money((sub?cfg.first:cfg.once)[count]);
        if(productPage){
          document.querySelectorAll('[data-pp-sticky-price],[data-pp-drawer-price]').forEach(e=>e.textContent=money((sub?cfg.first:cfg.once)[count]));
          document.querySelectorAll('[data-pp-drawer-package]').forEach(e=>e.textContent=count+' '+(count===1?'jar':'jars'));
          document.querySelectorAll('[data-pp-drawer-purchase-type]').forEach(e=>e.textContent=sub?'Subscribe & Save':'One-time purchase');
        }
        document.dispatchEvent(new CustomEvent('pp:purchase-state',{detail:{title:count+' '+(count===1?'jar':'jars'),cents:(sub?cfg.first:cfg.once)[count],purchaseType:sub?'subscription':'one-time'}}));
        const page=productPage;
        if(page){page.querySelectorAll('[data-pp-value-subscription-price]').forEach(e=>e.textContent=money(cfg.first[count]));page.querySelectorAll('[data-pp-value-refill-price]').forEach(e=>e.textContent=money(cfg.refill[count]));page.querySelectorAll('[data-pp-subscription-savings]').forEach(e=>e.textContent=`${cfg.discount[count]}% first delivery; ${({1:20,2:36,3:40})[count]}% on refills`);}
      }
      root.querySelectorAll('[data-pp-uniform-count]').forEach(b=>b.addEventListener('click',()=>{count=Number(b.dataset.ppUniformCount);render(true);}));
      root.querySelectorAll('[data-pp-uniform-method]').forEach(b=>b.addEventListener('click',()=>{method=b.dataset.ppUniformMethod;render();}));
      picks.forEach(p=>p.addEventListener('change',()=>render()));schedule.addEventListener('change',()=>render());
      add.addEventListener('click',async()=>{
        if(busy)return;busy=true;status.textContent='';render();
        try{
          const selected=picks.slice(0,count).map(p=>p.value),key=[...selected].sort().join('+');
          const id=count===1?cfg.formulas[selected[0]].id:count===2?cfg.pair[key]:cfg.trio[key];
          if(!id)throw Error('Please choose each jar.');
          const price=(method==='sub'?cfg.first:cfg.once)[count];
          let items=method==='once'&&count===2?selected.map(k=>({id:cfg.formulas[k].id,quantity:1})):[{id,quantity:1}];
          if(method==='sub')items[0].selling_plan=Number(schedule.value);
          if(typeof window.PuzzlePupOfferEngine?.addAndVerify!=='function')throw Error('The cart is still loading. Please try again.');
          const verified=await window.PuzzlePupOfferEngine.addAndVerify({items,expectedMaxIncrease:price,source:'supplement_uniform',offerId:`${count}-jar-${method}`,successEvent:'pp_offer_verified'});
          window.PuzzlePupAnalytics?.track('pp_add_to_cart',{variant_id:id,package:count+' jars',purchase_type:method==='sub'?'subscription':'one-time',selling_plan_id:method==='sub'?Number(schedule.value):undefined,quantity:1,effective_price:price,resulting_cart_total:verified.cart.total_price});
          await window.PuzzlePupOfferEngine.refreshCartSurfaces();
          const purchaseDrawer=document.querySelector('[data-pp-purchase-drawer]');
          if(purchaseDrawer&&!purchaseDrawer.hidden)window.PuzzlePupPdpDrawers?.close(purchaseDrawer,false);
          const drawer=document.querySelector('cart-drawer');
          if(drawer?.open)drawer.open();else location.assign('/cart');
        }catch(e){status.textContent=e.message||'Your package could not be added.';}
        finally{busy=false;render();}
      });
      render(true);
      const requestedPlan=Number(params.get('selling_plan'));
      if(cfg.plans[count].some(([id])=>id===requestedPlan)){method='sub';schedule.value=String(requestedPlan);render();}
    });
  }
  window.PuzzlePupUniformPurchase={init,config:cfg};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init,{once:true});else init();
  document.addEventListener('shopify:section:load',init);
})();
